import { NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup
} from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { chain } from "lodash";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse, Handelssortimentsgrupp, Mattslag } from "~app/common/models/constants";
import { ArbetsorderHandelssortimentsgruppDTO } from "~app/common/models/planera/ArbetsorderHandelssortimentsgruppDTO";
import { ITypSelectValue } from "~app/common/models/selectValueModel";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { NumberUtils } from "~app/common/utils/number-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { IProduktionsomraden } from "~app/features/planera/model/IProduktionsomraden";
import { ForkalkylsmallarService } from "~app/features/virkeskop/services/forkalkylsmallar.service";
import { ForkalkylRefreshMode, IForkalkylRefreshItem } from "../../forkalkylsmallar.component";
import { ForkalkylsmallDTO } from "../../models/ForkalkylsmallDTO";
import { ForkalkylsmallHandelssortimentDTO } from "../../models/ForkalkylsmallHandelssortimentDTO";
import { IForkalkylsmallBasdata } from "../../models/IForkalkylsmallBasdata";
import { TilldelaHandelssortimentsGruppDTO } from "../../models/TilldelaHandelssortimentsGruppDTO";
import { ForkalkylsmallValidation } from "./forkalkylsmall.validation";

@Component({
    selector: "rask-forkalkylsmall",
    templateUrl: "./forkalkylsmall.component.html",
    styleUrls: ["./forkalkylsmall.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SelectorComponent,
        CheckboxComponent,
        NgbPopover,
        NgClass,
        InputNumberComponent,
        IconButtonComponent,
        ActionButtonComponent,
        ButtonComponent
    ]
})
export class ForkalkylsmallComponent implements OnInit {
    activeProduktionsMalFilter: ArbetsorderHandelssortimentsgruppDTO[] = null;
    applyProduktionsMal: boolean;
    @Input() basdata: IForkalkylsmallBasdata;
    defaultMattslag = Mattslag.M3F;
    form: UntypedFormGroup;
    handelssortimentgrupper: TilldelaHandelssortimentsGruppDTO[];
    isDisabled = false;
    @Input() mall: ForkalkylsmallDTO;

    // Mellanlagring av dirty, då den är dyr när alla mallars dirtyness kallas på
    mallIsDirty = false;
    mattslaglista: ITypSelectValue[];
    produktionsMalFiltreradeGrupper: TilldelaHandelssortimentsGruppDTO[];
    produktionsomraden: IProduktionsomraden[];
    @Output() refreshRequest: EventEmitter<IForkalkylRefreshItem> = new EventEmitter<IForkalkylRefreshItem>();
    selectedProduktionsOmrade: number;
    validationContext: IValidationContext;

    private readonly busyService = inject(BusyService);
    private readonly confirmModalService = inject(RaskModalService);
    private readonly forkalkylsmallarService = inject(ForkalkylsmallarService);
    private readonly formBinder = inject(FormBinderService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly toastService = inject(ToastService);

    addRow(): void {
        if (this.mall.handelssortiment.length > 0) {
            const lastHS = this.mall.handelssortiment[this.mall.handelssortiment.length - 1];
            if (lastHS.handelssortimentId < 1 && lastHS.handelssortimentsGruppId < 1) {
                return;
            }
        }
        const newHS = {
            id: 0,
            tillaggAvdrag: 0,
            tackningsbidrag: 0,
            pefc: 0,
            mattslag: this.defaultMattslag,
            handelssortimentsGruppId: 0,
            handelssortimentId: 0,
            grundpris: 0,
            fsc: 0
        } as ForkalkylsmallHandelssortimentDTO;
        this.mall.handelssortiment.push(newHS);
        (this.form.controls.handelssortiment as UntypedFormArray).push(this.createFormArrayGroup(newHS));
    }

    canBeSaved(): boolean {
        return this.validationContext.validate();
    }

    getHSGroupValue(group: UntypedFormGroup): string {
        const item = group.value as ForkalkylsmallHandelssortimentDTO;
        const hsgrupp = this.handelssortimentgrupper.find(hsg => hsg.id === item.handelssortimentsGruppId);
        return hsgrupp?.namn ?? "";
    }

    getHSGroups(group: UntypedFormGroup): SimpleSelectionItem[] {
        const item = group.value as ForkalkylsmallHandelssortimentDTO;
        if (item.id > 0) {
            const grupp = this.handelssortimentgrupper.find(hsg => hsg.handelssortiment.some(hs => hs.id === item.handelssortimentId));
            return [
                {
                    id: grupp.sekvensnummer,
                    text: grupp.namn,
                    value: grupp.id
                }
            ];
        }
        if (this.produktionsMalFiltreradeGrupper === undefined) {
            return [];
        }
        const produktionsMalFiltreradeGrupperTemp =
            this.produktionsMalFiltreradeGrupper.map(hsg => ({
                id: hsg.sekvensnummer,
                text: hsg.namn,
                value: hsg.id
            }));
        this.sortAndRefreshHSGroups(produktionsMalFiltreradeGrupperTemp);
        return produktionsMalFiltreradeGrupperTemp;
    }

    getHSInactiveClass(hsData: UntypedFormGroup): string {
        if (this.isInactiveHS(hsData)) {
            return "fkm-row-hs-inactive";
        }
        return "";
    }

    getHSList(fgroup: UntypedFormGroup): SimpleSelectionItem[] {
        const item = fgroup.value as ForkalkylsmallHandelssortimentDTO;
        if (item.handelssortimentId > 0) {
            const group = this.handelssortimentgrupper.find(hsg => hsg.id === item.handelssortimentsGruppId);
            return group.handelssortiment
                .filter(hs => hs.id === item.handelssortimentId)
                .map(hs => ({
                    text: hs.namn,
                    value: hs.id
                }));
        }
        const grupp = this.produktionsMalFiltreradeGrupper.find(hsg => hsg.id === item.handelssortimentsGruppId);
        if (grupp === undefined) {
            return [];
        }
        let tempHandelssortimentList = grupp.handelssortiment;
        if (this.activeProduktionsMalFilter != null) {
            const tempGroup = this.activeProduktionsMalFilter.find(hsg => hsg.grupp === grupp.namn);
            const handelssortimentMedProduktionsmal = grupp.handelssortiment.filter(
                hs => tempGroup.handelssortiment.some(shs => shs.hasProduktionsmal && shs.handelssortimentId === hs.id));
            let allaHandelssortimentMedPoduktionsmal = [];
            this.activeProduktionsMalFilter.forEach(x =>
                allaHandelssortimentMedPoduktionsmal = allaHandelssortimentMedPoduktionsmal.concat(x.handelssortiment.filter(y => y.hasProduktionsmal)));
            let relaterade = [];
            allaHandelssortimentMedPoduktionsmal.forEach(x => {
                this.produktionsMalFiltreradeGrupper.forEach(y =>
                    relaterade = relaterade.concat(y.handelssortiment.filter(
                        z => z.relaterarTillHandelssortimentId === x.handelssortimentId && z.handelssortimentsgruppId === item.handelssortimentsGruppId))
                );
            });
            tempHandelssortimentList = handelssortimentMedProduktionsmal.concat(relaterade);
        }
        const alreadySelectedHSIds = this.mall.handelssortiment.map(x => x.handelssortimentId);
        const handelssortimentList = tempHandelssortimentList
            .filter(hs => !alreadySelectedHSIds.some(ahs => ahs === hs.id))
            .filter(hs => hs.aktiv)
            .map(hs => ({
                text: hs.namn,
                value: hs.id
            }));
        this.sortAndRefreshHSList(handelssortimentList);
        return handelssortimentList;
    }

    getHSValue(group: UntypedFormGroup): string {
        const item = group.value as ForkalkylsmallHandelssortimentDTO;
        const hsgrupp = this.handelssortimentgrupper.find(hsg => hsg.handelssortiment.some(hs => hs.id === item.handelssortimentId));
        const handelssortiment = hsgrupp?.handelssortiment.find(hs => hs.id === item.handelssortimentId);
        return handelssortiment?.namn ?? "";
    }

    getProduktionsomraden(): SimpleSelectionItem[] {
        if (this.produktionsomraden === undefined) {
            return [];
        }
        return this.produktionsomraden.map(po => ({
            text: po.namn,
            value: po.id,
            group: po.grupp
        }));
    }

    getmattslaglista(): SimpleSelectionItem[] {
        return this.mattslaglista.map(ms => Object.assign(new SimpleSelectionItem(), {
            text: ms.kortnamn,
            value: ms.kod
        }));
    }

    hsEditable(group: UntypedFormGroup): boolean {
        const item = group.value as ForkalkylsmallHandelssortimentDTO;
        if (item.id > 0) {
            return false;
        }

        if (item.handelssortimentId > 0) {
            return false;
        }
        return true;
    }

    hsGroupEditable(group: UntypedFormGroup): boolean {
        const item = group.value as ForkalkylsmallHandelssortimentDTO;
        if (item.id > 0) {
            return false;
        }

        if (item.handelssortimentsGruppId > 0) {
            return false;
        }
        return true;
    }

    isDataLoaded(): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.handelssortimentgrupper && this.mattslaglista && this.form) {
            return true;
        }
        return false;
    }

    isDirty(): boolean {
        if (this.form) {
            // Denna är för dyr att kalla på direkt.
            this.mallIsDirty = this.form.dirty;
        } else {
            this.mallIsDirty = false;
        }
        return this.mallIsDirty;
    }

    isInactiveHS(hsData: UntypedFormGroup): boolean {
        const hsID = hsData.get("handelssortimentId").value;
        const hsGID = hsData.get("handelssortimentsGruppId").value;

        if (hsID && hsID > 0) {

            if (hsGID && hsGID > 0) {
                if (this.handelssortimentgrupper.find(hsg => hsg.id === hsGID).handelssortiment.some(hs => hs.id === hsID && !hs.aktiv)) {
                    return true;
                }
            }
        }
        return false;
    }

    ngOnInit() {
        // Fixa basdata här
        this.mattslaglista = this.basdata.mattslagList;

        this.produktionsomraden = this.basdata.produktionsomraden;

        this.handelssortimentgrupper = this.basdata.handelssortimentGruppLista.filter(hsg => hsg.kod !== Handelssortimentsgrupp.Grot
            && hsg.kod !== Handelssortimentsgrupp.Stubbar
            && hsg.kod !== Handelssortimentsgrupp.Ovrigt);

        this.produktionsMalFiltreradeGrupper = this.handelssortimentgrupper.filter(hs => hs.handelssortiment && hs.handelssortiment.length > 0);

        // Här sätter vi hsgid då denna property inte kommer från db om det är hs som är valda.
        this.mall.handelssortiment.forEach(dto => {
            if (dto.handelssortimentId > 0) {
                const grupp = this.handelssortimentgrupper.find(hsg => hsg.handelssortiment.some(hs => hs.id === dto.handelssortimentId));
                dto.handelssortimentsGruppId = grupp.id;
            }
        });

        this.createForm();

        if (this.validationContext === undefined) {
            this.validationContext = ForkalkylsmallValidation.init(this.mall, this.basdata.forkalkylsmallar);
        }
    }

    poGrouper = (ssi: SimpleSelectionItem) => ssi ? ssi.group : undefined;

    produktionsMalChange(): void {
        if (this.selectedProduktionsOmrade !== null && this.selectedProduktionsOmrade !== undefined && this.applyProduktionsMal) {
            this.filterOnProduktionsMal();
        } else {
            this.unfilterOnProduktionsMal();
        }
    }

    removeMall(): void {
        this.confirmModalService.confirm(new ModalSettings("Borttagning av mall", `Är du säker på att du vill ta bort mall '${this.mall.namn}'?`))
            .subscribe((modalResponse) => {
                if (modalResponse === ConfirmDialogResponse.save) {
                    if (this.mall.id === 0) {
                        this.refreshParent(this.mall, ForkalkylRefreshMode.Delete);
                        this.toastService.add(`'${this.mall.namn}' togs bort`);
                    } else {
                        const commandObs = this.forkalkylsmallarService.deleteMall(this.mall);
                        this.busyService.addWork(commandObs).subscribe((response) => {
                            this.refreshParent(this.mall, ForkalkylRefreshMode.Delete);
                            this.toastService.add(`'${this.mall.namn}' togs bort`);
                        });
                    }
                }
            });
    }

    removeRow(hsFormGroup: UntypedFormGroup): void {
        const groupIndex = (this.form.controls.handelssortiment as UntypedFormArray).controls.indexOf(hsFormGroup);
        if (groupIndex > -1) {
            const group = (this.form.controls.handelssortiment as UntypedFormArray).at(groupIndex);
            (this.form.controls.handelssortiment as UntypedFormArray).removeAt(groupIndex);
            const hsGroupid = group.get("id").value;
            this.mall.handelssortiment = this.mall.handelssortiment.filter(hs => hs.id !== hsGroupid);
            this.form.markAsDirty();
        }
    }

    saveAll(useToast: boolean): void {
        this.validateUserInput();
        this.validationContext.validate();
        if (!this.validationContext.hasErrors(ValidationScopes.spara)) {
            const mallToSave = this.getMallFromForm();
            const commandObs = this.forkalkylsmallarService.updateMall(mallToSave);
            this.busyService.addWork(commandObs).subscribe((response) => {
                if (useToast) {
                    this.toastService.add(`'${mallToSave.namn}' sparades`);
                }
                this.refreshParent(mallToSave, ForkalkylRefreshMode.Save);
            });
        }
    }

    ssItemFormatter = (ssi: SimpleSelectionItem) => ssi ? ssi.value : undefined;

    ssItemViewFormatter = (ssi: SimpleSelectionItem) => ssi ? ssi.text : undefined;

    toggleProduktionsMal(val): void {
        this.applyProduktionsMal = val;
        if (this.selectedProduktionsOmrade !== null && this.selectedProduktionsOmrade !== undefined && val) {
            this.filterOnProduktionsMal();
        } else {
            this.unfilterOnProduktionsMal();
        }
    }

    private createForm(): void {
        const mall = this.mall;
        const sortedRows = chain(mall.handelssortiment)
            .orderBy([
                x => x.handelssortimentGruppSekvensnummer,
                x => x.handelssortimentsGruppId,
                x => x.handelssortimentNamn
            ])
            .value();
        this.form = this.formBuilder.group({
            namn: this.formBuilder.control(mall.namn),
            id: this.formBuilder.control(mall.id),
            handelssortiment: this.formBuilder.array(this.createFormArray(sortedRows))
        });
        this.form.markAsPristine();
        this.form.get("namn").valueChanges.subscribe((newNamn: string) => this.mall.namn = newNamn);
    }

    private createFormArray(rows: ForkalkylsmallHandelssortimentDTO[]): UntypedFormGroup[] {
        return rows.map(hs => this.createFormArrayGroup(hs));
    }

    private createFormArrayGroup(hs: ForkalkylsmallHandelssortimentDTO): UntypedFormGroup {
        return this.formBinder.CreateGroup(hs);
    }

    private filterOnProduktionsMal(): void {
        // Denna sätts om när Observablen har resolvats
        this.activeProduktionsMalFilter = null;
        const work = this.forkalkylsmallarService.getHSByProduktionsOmraden(this.selectedProduktionsOmrade);
        this.busyService.addWork(work).subscribe((ahgdto: ArbetsorderHandelssortimentsgruppDTO[]) => {
            const tempGroups = this.handelssortimentgrupper.slice();
            this.activeProduktionsMalFilter = ahgdto;
            const tempResult = tempGroups
                .filter(hsg => ahgdto.some(hsgf => hsgf.grupp === hsg.namn && (hsgf.hasProduktionsmal || this.finnsRelateradHandelssortimentIGruppen(hsgf))));
            this.produktionsMalFiltreradeGrupper = tempResult;
        });
    }

    private finnsRelateradHandelssortimentIGruppen(handelssortimentgrupp: ArbetsorderHandelssortimentsgruppDTO): boolean {
        if (!handelssortimentgrupp.handelssortiment) {
            return false;
        }
        return handelssortimentgrupp.handelssortiment
            .some(x => this.isRelatedToHandelsortimentWithProduktionsmal(x.relaterarTillHandelssortimentId));
    }

    private getMallFromForm(): ForkalkylsmallDTO {
        return this.form.value as ForkalkylsmallDTO;
    }

    private isRelatedToHandelsortimentWithProduktionsmal(relaterarTillHandelssortimentId: number): boolean {
        if (!relaterarTillHandelssortimentId) {
            return false;
        }
        return this.activeProduktionsMalFilter.some(x => x.handelssortiment.find(y => y.handelssortimentId === relaterarTillHandelssortimentId && y.hasProduktionsmal));
    }

    private refreshParent(mall: ForkalkylsmallDTO, mode: ForkalkylRefreshMode): void {
        this.refreshRequest.emit({
            mall: mall,
            mode: mode
        });
    }

    private sortAndRefreshHSGroups(produktionsMalFiltreradeGrupper: SimpleSelectionItem[]): void {
        if (!produktionsMalFiltreradeGrupper) {
            return;
        }
        produktionsMalFiltreradeGrupper.sort((a, b) => this.forkalkylsmallarService.orderBy(a.id, b.id));
    }

    private sortAndRefreshHSList(handelssortimentList: SimpleSelectionItem[]): void {
        if (!handelssortimentList) {
            return;
        }
        handelssortimentList.sort((a, b) => this.forkalkylsmallarService.orderBy(a.text, b.text));
    }

    private unfilterOnProduktionsMal(): void {
        this.activeProduktionsMalFilter = null;
        this.produktionsMalFiltreradeGrupper = this.handelssortimentgrupper.slice();
    }

    private validateUserInput(): void {
        (this.form.controls.handelssortiment as UntypedFormArray).controls.forEach((formGroup: UntypedFormGroup) => {
            Object.keys(formGroup.controls).forEach(key => {
                this.validateUserInputForControl(key, formGroup.controls[key] as UntypedFormControl);
            });
        });
    }

    private validateUserInputForControl(name: string, control: UntypedFormControl): void {
        if (name === "tillaggAvdrag") {
            const value = +control.value;
            if (value === null || value === undefined) {
                control.patchValue(0);
            }
            if (NumberUtils.isNumeric(value)) {
                control.patchValue(value);
            }
        } else if ([
            "fsc",
            "pefc",
            "grundpris",
            "tackningsbidrag"
        ].indexOf(name) > -1) {
            const value = +control.value;
            if (value === null || value === undefined) {
                control.patchValue(0);
            }
            if (NumberUtils.isNumeric(value)) {
                control.patchValue(Math.abs(value));
            }
        }
    }
}
